import { LogContext, Logger } from "@redbelly-tokeniser/mystake-logger-sdk";
import { getLastCorrelationId } from "@redbelly-tokeniser/mystake-api-sdk";

const logger = new Logger("console");

export type ErrorHandlerContext = {
  operation?: string;
  route?: string;
  correlationId?: string;
};

const buildLogMeta = (context?: ErrorHandlerContext): LogContext => {
  const meta: LogContext = {};
  if (context?.operation) meta.operation = context.operation;
  if (context?.route) meta.route = context.route;
  const correlationId = context?.correlationId ?? getLastCorrelationId();
  if (correlationId) meta.correlationId = correlationId;
  return meta;
};

// this logs the actual error to server logs, whilst returning a human readable message to the user
export const errorMessage = (
  error: unknown,
  message?: string,
  addDefaultMessage = true,
  context?: ErrorHandlerContext
): string => {
  let loggerErrorText = "An error occurred";

  if (error instanceof Error) {
    loggerErrorText = error.message;
  }

  if (message) {
    loggerErrorText = `${message}. This is the error message: ${loggerErrorText}`;
  }

  logger.error(loggerErrorText, buildLogMeta(context));

  const userErrorMessage = message || "An error occurred.";

  if (!addDefaultMessage) {
    return userErrorMessage;
  }

  return `${userErrorMessage} Please try again in a few minutes. If the error persists, please contact customer support for assistance.`;
};

/**
 * Validation failures arrive with every message in `payload` and only a count in `message` (yup validates
 * with `abortEarly: false`), so a two-field failure would otherwise read "2 errors occurred".
 */
export const apiErrorMessage = (response: { message?: string; payload?: unknown }, fallback: string): string => {
  if (Array.isArray(response.payload) && response.payload.every((item) => typeof item === "string")) {
    return response.payload.join(". ");
  }
  return response.message || fallback;
};

/**
 * Prefer the API's own error message (surfaced via `error.response.data.message`) so users see the
 * actual validation failure instead of a generic "something went wrong". Falls back to `error.message`
 * when the axios envelope isn't present.
 */
export const extractApiErrorMessage = (error: unknown): string | undefined => {
  if (error && typeof error === "object") {
    const { response } = error as { response?: { data?: { message?: unknown } } };
    const apiMessage = response?.data?.message;
    if (typeof apiMessage === "string" && apiMessage.trim().length > 0) {
      return apiMessage.trim();
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return undefined;
};
